// src/components/mascot/HalloweenDecor.js
// ─────────────────────────────────────────────────────────────
// Décor Halloween léger, inspiré de ce qu'ubee.com fait sur son site :
// une araignée qui descend par intervalles, un groupe de chauves-souris
// qui traverse l'écran de temps en temps (en vague, pas en ligne droite),
// un popup saisonnier dismissible.
// Fenêtre basée sur mois/jour (voir seasonal.js) — se réactive chaque
// année automatiquement, pas une date codée en dur à changer à la main.
//
// Volontairement PAS des animations en boucle infinie (voir le ménage
// qu'on vient de faire sur Elli/CPU) : chaque chauve-souris/araignée
// apparaît, joue son animation UNE fois, puis disparaît du DOM — le
// setInterval qui les déclenche est largement espacé (voir INTERVAL_MS),
// pas un coût continu.
// ─────────────────────────────────────────────────────────────

import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { isHalloweenPeriod } from "./seasonal";
import witchImg from "./images/halloween-witch.png";

const BAT_INTERVAL_MS = 60000; // un groupe de chauves-souris toutes les ~60s, pas un défilé continu
const SPIDER_INTERVAL_MS = 85000;
const WITCH_INTERVAL_MS = 75000;
const BATS_PER_GROUP = 4;
const FLYER_RETRY_MS = 4000; // si ça chevauche l'autre, on retente vite plutôt que d'attendre l'intervalle complet

const KEYFRAMES = `
@keyframes halloween-bat-fly {
  0%   { transform: translate(-10vw, 0px)    scale(0.9);  opacity: 0; }
  8%   { opacity: 1; }
  20%  { transform: translate(8vw, -30px)    scale(1); }
  35%  { transform: translate(28vw, 20px)    scale(1); }
  50%  { transform: translate(50vw, -28px)   scale(1.05); }
  65%  { transform: translate(70vw, 18px)    scale(1); }
  80%  { transform: translate(90vw, -22px)   scale(1.05); }
  92%  { opacity: 1; }
  100% { transform: translate(112vw, 4px)    scale(1.1);  opacity: 0; }
}
@keyframes halloween-wing-left {
  0%, 100% { transform: rotate(-15deg); }
  50% { transform: rotate(-70deg); }
}
@keyframes halloween-wing-right {
  0%, 100% { transform: rotate(15deg); }
  50% { transform: rotate(70deg); }
}
@keyframes halloween-spider-drop {
  0% { height: 0; }
  40% { height: var(--drop-height); }
  75% { height: var(--drop-height); }
  100% { height: 0; }
}
@keyframes halloween-spider-sway {
  0%, 100% { transform: translateX(-5px) rotate(-4deg); }
  50% { transform: translateX(5px) rotate(4deg); }
}
@keyframes halloween-witch-fly {
  0%   { transform: translate(-15vw, 12vh) rotate(-6deg) scale(0.9); opacity: 0; }
  8%   { opacity: 1; }
  25%  { transform: translate(15vw, -2vh)  rotate(4deg)  scale(1); }
  50%  { transform: translate(50vw, -14vh) rotate(-3deg) scale(1.05); }
  75%  { transform: translate(85vw, -2vh)  rotate(5deg)  scale(1); }
  92%  { opacity: 1; }
  100% { transform: translate(118vw, -8vh) rotate(-4deg) scale(1.1); opacity: 0; }
}
@keyframes halloween-witch-bob {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}
`;

function Bat({ topVw, duration, delay, scale }) {
  return (
    <Box
      sx={{
        position: "fixed", top: `${topVw}vh`, left: 0, zIndex: 1250, pointerEvents: "none",
        animation: `halloween-bat-fly ${duration}s ease-in-out ${delay}s forwards`,
      }}
    >
      {/* Deux ailes SÉPARÉES qui pivotent chacune sur son propre point
          d'attache — un vrai battement, pas l'émoji entier écrasé en
          scaleY. Formes volontairement simples (un seul triangle + une
          courbe par aile) après deux tentatives ratées de silhouette plus
          détaillée. */}
      <svg width={50 * scale} height={34 * scale} viewBox="0 0 60 40">
        <g style={{ animation: "halloween-wing-left 0.3s ease-in-out infinite", transformOrigin: "28px 18px" }}>
          <path d="M28,18 L2,7 Q15,19 28,23 Z" fill="#1a1a2e" />
        </g>
        <g style={{ animation: "halloween-wing-right 0.3s ease-in-out infinite", transformOrigin: "32px 18px" }}>
          <path d="M32,18 L58,7 Q45,19 32,23 Z" fill="#1a1a2e" />
        </g>
        <ellipse cx="30" cy="21" rx="5" ry="6.5" fill="#1a1a2e" />
        <path d="M27,17 L25,10 L29,15 Z M33,17 L35,10 L31,15 Z" fill="#1a1a2e" />
      </svg>
    </Box>
  );
}

function Spider({ rightPx, dropHeight, duration }) {
  return (
    <Box
      sx={{
        position: "fixed", top: 0, right: `${rightPx}px`, zIndex: 1250, pointerEvents: "none",
        "--drop-height": `${dropHeight}px`,
        animation: `halloween-spider-drop ${duration}s ease-in-out forwards`,
        width: "2px", bgcolor: "rgba(26,26,46,0.55)",
      }}
    >
      <Box sx={{ position: "absolute", bottom: -6, left: -23, animation: "halloween-spider-sway 1.7s ease-in-out infinite", transformOrigin: "top center" }}>
        <svg width="46" height="44" viewBox="0 0 46 44">
          <defs>
            <radialGradient id="halloween-spider-body" cx="35%" cy="30%" r="70%">
              <stop offset="0%" stopColor="#52526a" />
              <stop offset="100%" stopColor="#1a1a2e" />
            </radialGradient>
          </defs>
          {/* 8 pattes articulées — des courbes, pas des lignes droites */}
          <g stroke="#1a1a2e" strokeWidth="2.2" strokeLinecap="round" fill="none">
            <path d="M19 23 Q7 19 1 9" />
            <path d="M19 26 Q5 26 0 21" />
            <path d="M19 29 Q5 32 1 40" />
            <path d="M21 31 Q11 38 7 44" />
            <path d="M27 23 Q39 19 45 9" />
            <path d="M27 26 Q41 26 46 21" />
            <path d="M27 29 Q41 32 45 40" />
            <path d="M25 31 Q35 38 39 44" />
          </g>
          {/* Abdomen + marque sablier, clin d'œil à la veuve noire */}
          <ellipse cx="23" cy="28" rx="11" ry="9.5" fill="url(#halloween-spider-body)" />
          <path d="M23 21 L19.5 28 L23 35 L26.5 28 Z" fill="#c0392b" opacity="0.85" />
          {/* Tête + yeux */}
          <circle cx="23" cy="15" r="7" fill="url(#halloween-spider-body)" />
          <circle cx="19.5" cy="13" r="1.5" fill="#fff" />
          <circle cx="26.5" cy="13" r="1.5" fill="#fff" />
          <circle cx="19.5" cy="13" r="0.7" fill="#1a1a2e" />
          <circle cx="26.5" cy="13" r="0.7" fill="#1a1a2e" />
        </svg>
      </Box>
    </Box>
  );
}

function Witch({ topVh, duration, scale }) {
  return (
    <Box
      sx={{
        position: "fixed", top: `${topVh}vh`, left: 0, zIndex: 1250, pointerEvents: "none",
        animation: `halloween-witch-fly ${duration}s ease-in-out forwards`,
      }}
    >
      {/* Image fournie par Martin (silhouette détourée, fond transparent) —
          après plusieurs essais de SVG/icône/emoji à l'aveugle, impossible
          de faire mieux sans voir le rendu réel. Déjà orientée tête/balai
          vers la droite, dans le sens du vol — pas de flip nécessaire. */}
      <Box
        component="img"
        src={witchImg}
        alt=""
        sx={{
          width: `${150 * scale}px`, height: "auto", display: "block",
          animation: "halloween-witch-bob 1.4s ease-in-out infinite",
          filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.35))",
        }}
      />
    </Box>
  );
}

export default function HalloweenDecor() {
  const { i18n } = useTranslation();
  const fr = i18n.language === "fr";
  const [bats, setBats] = useState([]);
  const [spiders, setSpiders] = useState([]);
  const [witches, setWitches] = useState([]);
  const [showBanner, setShowBanner] = useState(false);

  // Verrou partagé bats/sorcière — la sorcière ne doit jamais voler pendant
  // qu'un groupe de chauves-souris traverse l'écran (demande explicite), et
  // vice-versa. Un simple timestamp "occupé jusqu'à" suffit, pas besoin
  // d'une lib d'état : chaque spawn qui tombe dedans se reporte de
  // quelques secondes au lieu de sauter tout un intervalle (50-65s).
  const flyingUntilRef = useRef(0);

  useEffect(() => {
    if (!isHalloweenPeriod()) return undefined;

    const dismissed = sessionStorage.getItem("ellipse_halloween_banner_dismissed");
    if (!dismissed) setShowBanner(true);

    const retryTimeouts = [];

    // Un groupe, pas une chauve-souris solitaire — chacune part d'une
    // hauteur/vitesse/délai légèrement différents pour ne pas avoir l'air
    // de clones collés, et suit une trajectoire en vague (voir keyframes).
    const spawnBatGroup = () => {
      if (Date.now() < flyingUntilRef.current) {
        retryTimeouts.push(setTimeout(spawnBatGroup, FLYER_RETRY_MS));
        return;
      }
      const groupId = Date.now() + Math.random();
      const baseTop = 6 + Math.random() * 28;
      const newBats = Array.from({ length: BATS_PER_GROUP }, (_, i) => ({
        id: `${groupId}-${i}`,
        topVw: baseTop + i * (4 + Math.random() * 3),
        duration: 6 + Math.random() * 1.5,
        delay: i * 0.25 + Math.random() * 0.2,
        scale: 0.85 + Math.random() * 0.3,
      }));
      flyingUntilRef.current = Date.now() + 9000; // couvre tout le groupe (max duration + delay + marge)
      setBats((b) => [...b, ...newBats]);
      setTimeout(() => setBats((b) => b.filter((x) => !newBats.some((n) => n.id === x.id))), 9000);
    };
    const spawnSpider = () => {
      const id = Date.now() + Math.random();
      setSpiders((s) => [...s, { id, rightPx: 60 + Math.random() * 200, dropHeight: 90 + Math.random() * 110, duration: 6 }]);
      setTimeout(() => setSpiders((s) => s.filter((x) => x.id !== id)), 7000);
    };
    const spawnWitch = () => {
      if (Date.now() < flyingUntilRef.current) {
        retryTimeouts.push(setTimeout(spawnWitch, FLYER_RETRY_MS));
        return;
      }
      const id = Date.now() + Math.random();
      const duration = 8 + Math.random() * 2;
      flyingUntilRef.current = Date.now() + duration * 1000 + 500;
      setWitches((w) => [...w, { id, topVh: 8 + Math.random() * 28, duration, scale: 1.15 + Math.random() * 0.35 }]);
      setTimeout(() => setWitches((w) => w.filter((x) => x.id !== id)), duration * 1000 + 500);
    };

    // setInterval à période FIXE fait inévitablement dériver les trois vers
    // le même instant de temps en temps (50s/65s/75s partagent des
    // multiples communs) — elles finissaient par arriver groupées après un
    // long silence. Un délai qui se re-tire aléatoirement (±30%) à chaque
    // cycle empêche ce genre de synchronisation périodique.
    const jitter = (baseMs, spread = 0.3) => baseMs * (1 - spread + Math.random() * spread * 2);

    let batTimerId, spiderTimerId, witchTimerId;
    const scheduleBat = () => { batTimerId = setTimeout(() => { spawnBatGroup(); scheduleBat(); }, jitter(BAT_INTERVAL_MS)); };
    const scheduleSpider = () => { spiderTimerId = setTimeout(() => { spawnSpider(); scheduleSpider(); }, jitter(SPIDER_INTERVAL_MS)); };
    const scheduleWitch = () => { witchTimerId = setTimeout(() => { spawnWitch(); scheduleWitch(); }, jitter(WITCH_INTERVAL_MS)); };

    // Un premier passage rapide pour confirmer que c'est actif — sans ça,
    // il faut attendre un intervalle COMPLET (50-75s) avant de voir quoi
    // que ce soit, ce qui donne l'impression que rien ne se passe.
    const firstBat = setTimeout(() => { spawnBatGroup(); scheduleBat(); }, 3000);
    const firstSpider = setTimeout(() => { spawnSpider(); scheduleSpider(); }, 6000);
    const firstWitch = setTimeout(() => { spawnWitch(); scheduleWitch(); }, 14000);

    return () => {
      clearTimeout(firstBat); clearTimeout(firstSpider); clearTimeout(firstWitch);
      clearTimeout(batTimerId); clearTimeout(spiderTimerId); clearTimeout(witchTimerId);
      retryTimeouts.forEach(clearTimeout);
    };
  }, []);

  if (!isHalloweenPeriod()) return null;

  return (
    <>
      <style>{KEYFRAMES}</style>
      {bats.map((b) => <Bat key={b.id} topVw={b.topVw} duration={b.duration} delay={b.delay} scale={b.scale} />)}
      {spiders.map((s) => <Spider key={s.id} rightPx={s.rightPx} dropHeight={s.dropHeight} duration={s.duration} />)}
      {witches.map((w) => <Witch key={w.id} topVh={w.topVh} duration={w.duration} scale={w.scale} />)}

      {showBanner && (
        <Box sx={{
          position: "fixed", bottom: 90, right: 20, zIndex: 1290, width: 280,
          bgcolor: "#d9611a", borderRadius: 3, p: 2.5, boxShadow: "0 10px 32px rgba(0,0,0,0.3)",
          border: "2px solid #1a1a2e",
        }}>
          <Typography sx={{ fontSize: 13, fontWeight: 900, letterSpacing: 1, color: "#1a1a2e", textTransform: "uppercase", mb: 0.75 }}>
            🎃 {fr ? "Halloween chez Ellipse" : "Halloween at Ellipse"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#fff", lineHeight: 1.5, mb: 1.5 }}>
            {fr ? "Un petit vent de Halloween souffle sur l'app jusqu'au 1er novembre." : "A little Halloween spirit around the app until November 1st."}
          </Typography>
          <Button
            size="small"
            onClick={() => { setShowBanner(false); sessionStorage.setItem("ellipse_halloween_banner_dismissed", "1"); }}
            sx={{ bgcolor: "#1a1a2e", color: "#f5d377", fontWeight: 700, fontSize: 12, textTransform: "none", "&:hover": { bgcolor: "#2d2d4e" } }}
          >
            {fr ? "Fermer" : "Close"}
          </Button>
        </Box>
      )}
    </>
  );
}
